import { FC } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { getUrlParam } from 'common/util/location';
import * as K from 'common/constants';
import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ELEMENT from '../../common/element';
import { GET_INVITE_INFO_QUERY } from './queries';
import { ActionType, InviteLinkInfoType } from './types';
import { LinkFormCreateAccount } from './components/LinkFormCreateAccount';
import { InviteTokenError } from './components/InviteTokenError';
import { LinkAccountButton } from './components/LinkAccountButton';
import './styles.scss';

const AcceptLinkInvitePageInner: FC<{ token: string }> = ({ token }) => {
  const { data, loading, error } = useQuery<{ parseInviteToken: InviteLinkInfoType }>(GET_INVITE_INFO_QUERY, {
    variables: { token },
  });

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (error || !data) {
    return <InviteTokenError error={error} />;
  }

  const {
    parseInviteToken: { linkAccountActionType, inviter, invitee },
  } = data;

  const isCreate = linkAccountActionType === ActionType.CREATE;

  const pageTitle = `${isCreate ? 'Create' : 'Link'} Your ${K.PRODUCT_NAME} Account`;
  const inviteText = `You have been invited ${isCreate ? 'by:' : 'to link your account to:'}`;
  const linkAccountAdditionalText = !isCreate && (
    <span className="AcceptLinkInvite__InviteInfoLink">
      {`This will give you access to the hosts, saved searches, dashboard, and alerts set up by ${inviter}.`}
    </span>
  );

  return (
    <>
      <Page docTitle="Link Account" pageModifier="AcceptLinkInvite__Title">
        <PageHeader title={pageTitle} />
        <div className="AcceptLinkInvite__Container">
          <h4
            className="AcceptLinkInvite__InviteInfo AcceptLinkInvite__InviteInfo--Bold"
            data-test-id={ELEMENT.acceptLinkInviteInvitee}
          >
            {invitee}
          </h4>
          <h4 className="AcceptLinkInvite__InviteInfo">{inviteText}</h4>
          <h4
            className="AcceptLinkInvite__InviteInfo AcceptLinkInvite__InviteInfo--Bold"
            data-test-id={ELEMENT.acceptLinkInviteInviter}
          >
            {inviter}
          </h4>
          {linkAccountAdditionalText}
          {isCreate ? <LinkFormCreateAccount token={token} /> : <LinkAccountButton token={token} />}
        </div>
      </Page>
    </>
  );
};

/**
 * The page for accepting an invitation to link an account.
 */
const AcceptLinkInvitePage: FC = () => {
  const location = useLocation();
  // TODO: Fix once we upgrade react-router to next major version. There is an incompatibility between history and react-router
  const search = location.search || location.location?.search;
  const token = getUrlParam(search || '', K.URL_PARAM.INVITE_TOKEN);
  if (!token) {
    return <InviteTokenError isNoToken />;
  }
  return <AcceptLinkInvitePageInner token={token} />;
};

export default AcceptLinkInvitePage;
